/**
 * useResendVerification — TanStack mutation over
 * `POST /organizations/:slug/users/:userId/resend-verification`.
 *
 * 202 success returns the redacted recipient. 422 fires when the BE
 * detects the email is already verified — the wizard surfaces that as
 * a distinct kind so the caller can disable the affordance instead of
 * re-firing.
 *
 * Error envelope:
 *   - 'alreadyVerified' — 422 (BE detected the verified flag is already set)
 *   - 'forbidden'       — 403 (missing identity.users.verification.resend)
 *   - 'notFound'        — 404
 *   - 'rateLimited'     — 429
 *   - 'error'           — anything else
 *
 * On success the audit feed is invalidated — the BE emits
 * `user.verification_resent`.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { buildUserAuditQueryKey } from './use-user-audit.js';

export interface ResendVerificationResult {
  readonly sentTo: string;
}

export type ResendVerificationError =
  | { readonly kind: 'alreadyVerified' }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'notFound' }
  | { readonly kind: 'rateLimited' }
  | { readonly kind: 'error' };

export interface UseResendVerificationResult {
  readonly mutateAsync: () => Promise<ResendVerificationResult>;
  readonly isPending: boolean;
}

export function useResendVerification(
  userId: string | undefined,
): UseResendVerificationResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;
  const queryClient = useQueryClient();

  const mutation = useMutation<
    ResendVerificationResult,
    ResendVerificationError,
    undefined
  >({
    mutationFn: async () => {
      if (orgSlug === null || userId === undefined) {
        throw { kind: 'error' } satisfies ResendVerificationError;
      }
      const { response, data } = await apiClient.POST(
        '/organizations/{slug}/users/{userId}/resend-verification',
        {
          params: { path: { slug: orgSlug, userId } },
        },
      );
      if (response.status === 422) {
        throw { kind: 'alreadyVerified' } satisfies ResendVerificationError;
      }
      if (response.status === 403) {
        throw { kind: 'forbidden' } satisfies ResendVerificationError;
      }
      if (response.status === 404) {
        throw { kind: 'notFound' } satisfies ResendVerificationError;
      }
      if (response.status === 429) {
        throw { kind: 'rateLimited' } satisfies ResendVerificationError;
      }
      if (!response.ok || data === undefined) {
        throw { kind: 'error' } satisfies ResendVerificationError;
      }
      const dto = data as { sent_to?: unknown };
      const sentTo = typeof dto.sent_to === 'string' ? dto.sent_to : '';
      return { sentTo };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: buildUserAuditQueryKey(orgSlug, userId),
      });
    },
  });

  return {
    mutateAsync: () => mutation.mutateAsync(undefined),
    isPending: mutation.isPending,
  };
}
